import React, { useEffect, useState } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { blogApis } from "../../../Data/axios.js";
import TicketsTable from "../TicketsTable.jsx";
import config from "../../../Data/AuthUser.js";
import ReusableTable from "../ReusableTable.jsx";
import DashboardBlogsViewMore from "./DashboardBlogsViewMore.jsx";
import DashboardBlogsEdit from "./DashboardBlogsEdit.jsx";

const DashboardBlogs = () => {
  const [dashBlogs, setDashBlogs] = useState([]); // Initialize as an empty array
  const [loading, setLoading] = useState(true);
  // const [creator, setCreator] = useState({
  //   name: "",
  //   image: null,
  //   role: "",
  //   role_name: "",
  // });
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [dropdownIndex, setDropdownIndex] = useState(null); // State to manage dropdown visibility
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [queries, setQueries] = useState({
    page: 1,
    per_page: 10,
  });
  const [newBlog, setNewBlog] = useState({
    title: "",
    description: "",
    type: 1, // Defaults to '1: blog'
    image: "",
    youtube: "",
    link: "",
  });

  const [filters, setFilters] = useState({
    status: "",
    type: "",
  });

  const headers = [
    { key: "id", label: "ID", sortable: true },
    { key: "author", label: "Author", sortable: true },
    { key: "title", label: "Title", sortable: true },
    { key: "description", label: "Description", sortable: true },
    { key: "type", label: "Type", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  const IMAGE_API_URL = config.apiImageUrl;

  const data = dashBlogs.map((blog, index) => ({
    id: blog.id,
    title: blog.title,
    description: blog.description
      ? blog.description.substring(0, 25) +
        (blog.description.length > 25 ? "..." : "")
      : "",
    type:
      blog.type === 1
        ? "Blog"
        : blog.type === 2
        ? "News"
        : blog.type === 3
        ? "Tutorials"
        : "Video",
    status: blog.status === 1 ? "Active" : "Inactive",
    author: (
      <div className="flex items-center gap-3">
        {blog.creator && blog.creator.image && (
          <img
            src={`${IMAGE_API_URL}${blog.creator.image}`}
            alt={blog.creator?.name || "Creator"}
            className="w-10 h-10 rounded-full object-cover"
          />
        )}
        <div>
          <div>{blog.creator?.name || "Name"}</div>
          <div className="text-xs text-gray-500">
            {blog.creator?.role || "Role"}
          </div>
        </div>
      </div>
    ),
    creator: {
      name: blog.creator?.name || "Name",
      image: blog.creator?.image || null,
      role: blog.creator?.role || "Role",
      role_name: blog.creator?.role_name || "Role Name",
    },
    youtube: blog.youtube,
    link: blog.link,
    image: blog.image,
    created_by: blog.created_by,
    // Add all fields that need to be passed to view/edit modals
    rawData: blog // Store the complete blog object for access in modal components
  }));

  // Fetch blogs on component
  const fetchData = async (filterOverrides = null) => {
    try {
      // Use filterOverrides if provided, otherwise use the filters from state
      const currentFilters = filterOverrides || filters;

      // Combine queries and filters into a single parameters object
      const params = {
        ...queries,
        ...(currentFilters.status && { status: currentFilters.status }),
        ...(currentFilters.type && { type: currentFilters.type }),
      };

      console.log("Fetching with parameters:", params);
      const response = await blogApis.getDashboardBlogs(params);
      console.log("Response: ", response);
      setDashBlogs(response?.data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch site data");
      console.error("Error fetching blogs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const refreshData = async () => {
    setLoading(true);
    fetchData();
  };

  useEffect(() => {
    console.log("Queries changed, fetching data with new queries:", queries);
    refreshData();
  }, [queries, filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: "",
      type: "",
    });
  };

  const onPageChange = (newPage) => {
    setQueries((prev) => ({ ...prev, page: newPage }));
  };

  const onPerPageChange = (newPerPage) => {
    setQueries((prev) => ({ ...prev, per_page: newPerPage }));
  };

  // Handle input changes for the modal form
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files?.length > 0) {
      setNewBlog((prev) => ({ ...prev, image: files[0] })); // Store the file
    } else {
      setNewBlog((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Validate form fields
  const validateForm = () => {
    // Check required fields
    if (!newBlog.title.trim()) {
      setError("Title is required");
      return false;
    }
    if (!newBlog.description.trim()) {
      setError("Description is required");
      return false;
    }
    if (!newBlog.image) {
      setError("Image is required");
      return false;
    }

    // Clear any previous error
    setError(null);
    return true;
  };

  // Handle form submission to add a new blog
  const handleAddPost = async () => {
    // Validate form before submission
    if (!validateForm()) {
      return; // Stop if validation fails
    }

    console.log("Attempting to add post: ", newBlog);
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("title", newBlog.title);
      formData.append("description", newBlog.description);
      formData.append("type", newBlog.type);
      formData.append("image", newBlog.image); // Append the file
      formData.append("youtube", newBlog.youtube);
      formData.append("link", newBlog.link);

      await blogApis.add(newBlog);
      await fetchData();

      // Refresh the blog list
      setShowModal(false);
      setNewBlog({
        title: "",
        description: "",
        type: 1,
        image: "",
        youtube: "",
        link: "",
      });
    } catch (err) {
      setError(err.message || "Failed to add new post");
      console.log("Error adding blog:", err.response.data);
    } finally {
      setLoading(false);
    }
  };

  // // Close the dropdown
  // const closeDropdown = () => setDropdownIndex(null);

  // // Handle outside click to close the dropdown
  // useEffect(() => {
  //   const handleClickOutside = () => closeDropdown();
  //   window.addEventListener("click", handleClickOutside);
  //   return () => window.removeEventListener("click", handleClickOutside);
  // }, []);

  // // Prevent dropdown closure when clicking inside
  // const handleDropdownClick = (e) => e.stopPropagation();

    // Handle table actions
    // const handleTableAction = (action, rowData) => {
    //   setSelectedBlog(rowData);
      
    //   if (action === 'view') {
    //     setShowViewModal(true);
    //   } else if (action === 'edit') {
    //     setShowEditModal(true);
    //   } else if (action === 'delete') {
    //     handleDelete(rowData.id);
    //   }
    // };
    // Handle table actions
const handleTableAction = (action, rowData) => {
  console.log("Table action received:", action, rowData);
  
  if (action === 'view') {
    // Ensure we're getting the rawData or complete data
    const blogData = rowData.rawData || rowData;
    console.log("Opening view modal with data:", blogData);
    setSelectedBlog(blogData);
    setShowViewModal(true);
  } 
  else if (action === 'edit') {
    const blogData = rowData.rawData || rowData;
    console.log("Opening edit modal with data:", blogData);
    setSelectedBlog(blogData);
    setShowEditModal(true);
  } 
  else if (action === 'delete') {
    const blogId = rowData.id || (rowData.rawData && rowData.rawData.id);
    if (blogId) {
      handleDelete(blogId);
    } else {
      console.error("Could not find ID for deletion");
    }
  }
};
  
    // Handle edit blog save
    const handleEditSave = async (formData) => {
      try {
        setLoading(true);
        await blogApis.updateById(selectedBlog.id, formData);
        setShowEditModal(false);
        await refreshData();
      } catch (error) {
        console.error("Failed to update blog:", error);
        setError("Failed to update blog: " + (error.message || "Unknown error"));
      } finally {
        setLoading(false);
      }
    };
  
    // Handle delete blog
    const handleDelete = async (id) => {
      if (window.confirm("Are you sure you want to delete this blog?")) {
        try {
          setLoading(true);
          await blogApis.remove(id);
          await refreshData();
        } catch (error) {
          console.error("Failed to delete blog:", error);
          setError("Failed to delete blog: " + (error.message || "Unknown error"));
        } finally {
          setLoading(false);
        }
      }
    };

  return (
    <div className="bg-gray-200 min-h-screen text-black">
      <div className="w-full bg-white shadow-sm mb-6">
        <div className="container mx-auto py-3 px-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-500 mr-2">
                Status:
              </label>
              <select
                name="status"
                className="border rounded p-1.5 text-gray-800 text-sm"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </select>
            </div>

            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-500 mr-2">
                Type:
              </label>
              <select
                name="type"
                className="border rounded p-1.5 text-gray-800 text-sm"
                value={filters.type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="1">Blog</option>
                <option value="2">News</option>
                <option value="3">Tutorial</option>
                <option value="4">Video</option>
              </select>
            </div>

            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-500 mr-2">From:</label>
              <input
                type="date"
                name="start_date"
                className="border rounded p-1.5 text-gray-800 text-sm"
              />
            </div>
            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-500 mr-2">To:</label>
              <input
                type="date"
                name="end_date"
                className="border rounded p-1.5 text-gray-800 text-sm"
              />
            </div>
            <button
              type="button"
              className="ml-auto bg-gray-100 text-gray-600 px-3 py-1.5 rounded text-sm hover:bg-gray-200"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="container mx-auto px-4">
        {/* Header with Add Blog button */}
        <div className="flex justify-between items-center mb-4 md:mx-6 mt-1">
          <h1 className="text-2xl font-bold font-ubuntu">Blogs</h1>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            onClick={() => setShowModal(true)}
          >
            Add Blog
          </button>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-10 text-center md:mx-6 mb-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading blogs...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm p-10 text-center md:mx-6 mb-8">
            <div className="text-red-500 mb-2 text-xl">Error</div>
            <p>{error}</p>
            <button
              onClick={refreshData}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Try Again
            </button>
          </div>
        ) : dashBlogs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-10 text-center md:mx-6 mb-8">
            <div className="text-gray-400 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-medium text-gray-700 mb-2">
              No Blogs Found
            </h3>
            <p className="text-gray-500 mb-6">
              {filters.status || filters.type
                ? "No blogs match your current filter criteria."
                : "There are no blogs available yet."}
            </p>
            {(filters.status || filters.type) && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 mr-3"
              >
                Clear Filters
              </button>
            )}
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Add Your First Blog
            </button>
          </div>
        ) : (
          <div className="h-[50vh]">
            <ReusableTable
              headers={headers}
              data={data}
              rowKey="id"
              defaultSortKey="id"
              tableClassName=""
              headerClassName="text-gray-700"
              rowClassName="hover:bg-gray-200"
              tableApis={blogApis}
              onTableUpdate={refreshData}
              onPageChange={onPageChange}
              onPerPageChange={onPerPageChange}
              queries={queries}
              type="blogs"
              onAction={handleTableAction}
            />
          </div>
        )}
      </div>

      {/* Add Blog Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-[0vw] md:w-1/6"></div>
          <div className="bg-white p-6 rounded shadow-lg w-full md:w-[85vw] h-[88vh] flex flex-col">
            <h2 className="text-lg font-bold text-gray-800 mb-4 text-center">
              Add New Blog
            </h2>
            <div className="border-t border-gray-200 mb-6"></div>

            {/* Display validation errors */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p>{error}</p>
              </div>
            )}

            <div className="flex flex-col space-y-8 flex-grow">
              {/* Flex layout for headers and inputs */}
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">Title</p>
                <input
                  type="text"
                  name="title"
                  placeholder="Title"
                  value={newBlog.title}
                  onChange={handleInputChange}
                  className="w-2/3 border rounded p-2 text-gray-800"
                  required
                />
              </div>
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">
                  Description
                </p>
                <textarea
                  name="description"
                  placeholder="Description"
                  value={newBlog.description}
                  onChange={handleInputChange}
                  className="w-2/3 border rounded p-2 text-gray-800"
                  rows="5"
                  required
                />
              </div>
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">Type</p>
                <select
                  name="type"
                  className="w-2/3 border rounded p-2 text-gray-800"
                  value={newBlog.type}
                  onChange={handleInputChange}
                >
                  <option value={1}>Blog</option>
                  <option value={2}>News</option>
                  <option value={3}>Tutorial</option>
                  <option value={4}>Video</option>
                </select>
              </div>
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">Image</p>
                <input
                  type="file"
                  name="image"
                  accept="image/jpeg, image/png, image/jpg, image/gif"
                  onChange={handleInputChange}
                  className="w-2/3 border rounded p-2 text-gray-800"
                  required
                />
              </div>
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">
                  YouTube URL
                </p>
                <input
                  type="url"
                  name="youtube"
                  placeholder="YouTube URL"
                  value={newBlog.youtube}
                  onChange={handleInputChange}
                  pattern="https?://.*"
                  className="w-2/3 border rounded p-2 text-gray-800"
                />
              </div>
              <div className="flex">
                <p className="text-sm font-medium text-gray-500 w-1/3">Link</p>
                <input
                  type="url"
                  name="link"
                  placeholder="Link"
                  value={newBlog.link}
                  onChange={handleInputChange}
                  pattern="https?://.*"
                  className="w-2/3 border rounded p-2 text-gray-800"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-start space-x-4">
              <button
                type="button"
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-lg"
                // onClick={(e) => {
                //   e.preventDefault();
                //   handleAddPost();
                // }}
                onClick={handleAddPost}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
            {/* View Blog Modal */}
            {showViewModal && selectedBlog && (
        <DashboardBlogsViewMore 
          data={selectedBlog.rawData || selectedBlog} 
          onClose={() => setShowViewModal(false)} 
        />
      )}

      {/* Edit Blog Modal */}
      {showEditModal && selectedBlog && (
        <DashboardBlogsEdit 
          data={selectedBlog.rawData || selectedBlog}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

export default DashboardBlogs;
